// src/pages/AnalyticsDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Tabs, Tab, Select, MenuItem, FormControl, InputLabel,
  Button, Chip, LinearProgress, Alert
} from '@mui/material';
import {
  BarChart, ShowChart, TrendingUp, Psychology,
  CalendarMonth, Download, Assessment
} from '@mui/icons-material';
import { getData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

// Імпортуємо компоненти графіків
import ProductionTrendChart from '../components/analytics/ProductionTrendChart';
import PlanVsActualChart from '../components/analytics/PlanVsActualChart';
import EfficiencyAnalysis from '../components/analytics/EfficiencyAnalysis';
import AIRecommendations from '../components/analytics/AIRecommendations';

const AnalyticsDashboard = ({ user }) => {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [operatorReports, setOperatorReports] = useState([]);
  const [technicianReports, setTechnicianReports] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const plansData = await getData('production_plans') || [];
      const opReports = await getData('operator_reports') || [];
      const techReports = await getData('technician_reports') || [];
      
      setPlans(plansData);
      setOperatorReports(opReports);
      setTechnicianReports(techReports);
      
      // Автоматично вибираємо активний план поточного року
      const currentYearPlan = plansData.find(plan => 
        plan.year === new Date().getFullYear() && plan.status === 'active'
      );
      setSelectedPlan(currentYearPlan || (plansData.length > 0 ? plansData[0] : null));
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Отримуємо доступні роки для фільтрації
  const availableYears = [...new Set(plans.map(plan => plan.year))].sort((a, b) => b - a);
  if (availableYears.length === 0) {
    availableYears.push(new Date().getFullYear());
  }

  // Фільтруємо дані за вибраний рік
  const filteredReports = operatorReports.filter(report => {
    try {
      const reportDate = new Date(report.date);
      return reportDate.getFullYear() === selectedYear;
    } catch {
      return false;
    }
  });

  // 📈 РОЗРАХУНОК КЛЮЧОВИХ ПОКАЗНИКІВ
  const calculateKPIs = () => {
    const monthlyData = Array(12).fill(0).map(() => ({
      tunnel: 0,
      washing: 0,
      total: 0,
      count: 0
    }));

    // Агрегуємо дані по місяцях
    filteredReports.forEach(report => {
      try {
        const month = new Date(report.date).getMonth();
        const tunnelProduction = report.tunnelProduction || 0;
        const washProduction = calculateWashProduction(report);
        
        monthlyData[month].tunnel += tunnelProduction;
        monthlyData[month].washing += washProduction;
        monthlyData[month].total += tunnelProduction + washProduction;
        monthlyData[month].count += 1;
      } catch (error) {
        console.error('Error processing report:', error);
      }
    });

    // Розраховуємо середні значення
    const monthlyAverages = monthlyData.map(month => ({
      tunnel: month.count > 0 ? month.tunnel / month.count : 0,
      washing: month.count > 0 ? month.washing / month.count : 0,
      total: month.count > 0 ? month.total / month.count : 0
    }));

    // Загальні показники
    const totalTunnel = monthlyData.reduce((sum, month) => sum + month.tunnel, 0);
    const totalWashing = monthlyData.reduce((sum, month) => sum + month.washing, 0);
    const totalProduction = totalTunnel + totalWashing;

    // Порівняння з планом
    const planTarget = selectedPlan ? (selectedPlan.tunnelTarget || 0) + (selectedPlan.washTarget || 0) : 0;
    const planProgress = planTarget > 0 ? (totalProduction / planTarget) * 100 : 0;

    return {
      monthlyAverages,
      totalTunnel,
      totalWashing,
      totalProduction,
      planTarget,
      planProgress,
      monthlyData
    };
  };

  const calculateWashProduction = (report) => {
    return (report.washCycles20kg || 0) * 20 + 
           (report.washCycles40kg || 0) * 40 + 
           (report.washCycles60kg || 0) * 55;
  };

  // 🤖 AI АНАЛІЗ ЕФЕКТИВНОСТІ
  const getEfficiencyAnalysis = () => {
    const kpis = calculateKPIs();
    const analysis = {
      overallEfficiency: 0,
      trends: [],
      recommendations: [],
      alerts: []
    };

    // Аналіз ефективності
    if (selectedPlan) {
      analysis.overallEfficiency = kpis.planProgress;
      
      // Виявлення трендів
      const recentMonths = kpis.monthlyAverages.slice(-3).filter(d => d.total > 0);
      const previousMonths = kpis.monthlyAverages.slice(-6, -3).filter(d => d.total > 0);
      
      if (recentMonths.length >= 2 && previousMonths.length >= 2) {
        const recentAvg = recentMonths.reduce((sum, month) => sum + month.total, 0) / recentMonths.length;
        const previousAvg = previousMonths.reduce((sum, month) => sum + month.total, 0) / previousMonths.length;
        
        if (previousAvg > 0) {
          const change = ((recentAvg - previousAvg) / previousAvg) * 100;
          const trend = change > 10 ? 'growth' : change > -5 ? 'stable' : 'decline';
          
          analysis.trends.push({
            type: trend,
            message: `analytics_trend_${trend}`,
            percentage: Math.round(Math.abs(change))
          });
        }
      }

      // Рекомендації
      if (kpis.planProgress < 80) {
        analysis.recommendations.push('analytics_recommendation_low_progress');
      }

      if (kpis.totalWashing > 0 && (kpis.totalWashing / kpis.totalTunnel) < 0.3) {
        analysis.recommendations.push('analytics_recommendation_washing_capacity');
      }

      // Сповіщення
      const currentMonth = new Date().getMonth();
      const monthlyTarget = selectedPlan ? (selectedPlan.tunnelTarget + selectedPlan.washTarget) / 12 : 0;
      
      if (monthlyTarget > 0 && kpis.monthlyAverages[currentMonth]?.total < monthlyTarget * 0.7) {
        analysis.alerts.push('analytics_alert_current_month');
      }
    }

    // Загальні рекомендації якщо немає плану
    if (!selectedPlan && analysis.recommendations.length === 0) {
      analysis.recommendations.push('analytics_recommendation_create_plan');
    }

    return analysis;
  };

  const kpis = calculateKPIs();
  const efficiencyAnalysis = getEfficiencyAnalysis();

  // Перевірка на відсутність даних
  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 3 }}>
          {t('analytics_title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Loading analytics data...
        </Typography>
      </Box>
    );
  }

  if (plans.length === 0 && operatorReports.length === 0) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 3 }}>
          {t('analytics_title')}
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography>
            {t('analytics_no_data_message')}
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <CalendarMonth sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('analytics_no_production_plans')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('analytics_create_plans_first')}
                </Typography>
                <Button 
                  variant="contained" 
                  component="a" 
                  href="/production-planning"
                >
                  {t('production_create_new_plan')}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('analytics_no_reports_data')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('analytics_need_operator_reports')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок та фільтри */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('analytics_title')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>{t('analytics_year')}</InputLabel>
            <Select
              value={selectedYear}
              label={t('analytics_year')}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {availableYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>{t('analytics_plan')}</InputLabel>
            <Select
              value={selectedPlan?.id || ''}
              label={t('analytics_plan')}
              onChange={(e) => {
                const plan = plans.find(p => p.id === e.target.value);
                setSelectedPlan(plan);
              }}
            >
              {plans
                .filter(plan => plan.year === selectedYear)
                .map(plan => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {t('production_plan')} {plan.year} - {t(`production_types_${plan.type}`)}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>

          <Button startIcon={<Download />} variant="outlined">
            {t('analytics_export')}
          </Button>
        </Box>
      </Box>

      {/* AI Сповіщення */}
      {efficiencyAnalysis.alerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology />
            <Box>
              <Typography fontWeight="500">
                {t('analytics_ai_alerts')}
              </Typography>
              {efficiencyAnalysis.alerts.map((alert, index) => (
                <Typography key={index} variant="body2">
                  • {t(alert)}
                </Typography>
              ))}
            </Box>
          </Box>
        </Alert>
      )}

      {/* Ключові показники */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUp color="primary" />
                <Typography variant="h6">{t('analytics_total_production')}</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {Math.round(kpis.totalProduction / 1000)}k
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('analytics_units_kg')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BarChart color="success" />
                <Typography variant="h6">{t('analytics_plan_progress')}</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {Math.round(kpis.planProgress)}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={kpis.planProgress}
                color={kpis.planProgress >= 80 ? 'success' : kpis.planProgress >= 60 ? 'warning' : 'error'}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShowChart color="info" />
                <Typography variant="h6">{t('analytics_efficiency')}</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {Math.round(efficiencyAnalysis.overallEfficiency)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('analytics_overall')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarMonth color="secondary" />
                <Typography variant="h6">{t('analytics_data_points')}</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {filteredReports.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('analytics_reports_analyzed')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Навігація по розділах */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab value="overview" label={t('analytics_tab_overview')} />
        <Tab value="trends" label={t('analytics_tab_trends')} />
        <Tab value="comparison" label={t('analytics_tab_comparison')} />
        <Tab value="efficiency" label={t('analytics_tab_efficiency')} />
        <Tab value="recommendations" label={t('analytics_tab_recommendations')} />
      </Tabs>

      {/* Вміст розділів */}
      {activeTab === 'overview' && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <ProductionTrendChart 
              data={kpis.monthlyAverages}
              selectedYear={selectedYear}
              t={t}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <AIRecommendations 
              analysis={efficiencyAnalysis}
              t={t}
            />
          </Grid>
        </Grid>
      )}

      {activeTab === 'trends' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ProductionTrendChart 
              data={kpis.monthlyAverages}
              selectedYear={selectedYear}
              showDetails={true}
              t={t}
            />
          </Grid>
        </Grid>
      )}

      {activeTab === 'comparison' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PlanVsActualChart 
              plan={selectedPlan}
              actualData={kpis.monthlyData}
              selectedYear={selectedYear}
              t={t}
            />
          </Grid>
        </Grid>
      )}

      {activeTab === 'efficiency' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EfficiencyAnalysis 
              kpis={kpis}
              analysis={efficiencyAnalysis}
              t={t}
            />
          </Grid>
        </Grid>
      )}

      {activeTab === 'recommendations' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AIRecommendations 
              analysis={efficiencyAnalysis}
              showDetailed={true}
              t={t}
            />
          </Grid>
        </Grid>
      )}

      {/* Інформація про дані */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('analytics_data_last_updated')}: {new Date().toLocaleDateString()} • 
          {t('analytics_reports_count')}: {filteredReports.length} • 
          {t('analytics_plans_count')}: {plans.filter(p => p.year === selectedYear).length}
        </Typography>
      </Box>
    </Box>
  );
};

export default AnalyticsDashboard;